import * as core from "@graffer-inc/cloud-gov-core";
import {
  CloudGovFormSchema,
  SmartApplyFormSchemaValidator,
  ValidateOpt,
} from "@graffer-inc/cloud-gov-core";
import { FormSchema } from "@graffer-inc/form-schema";
import { InitialMetaInfoMapping } from "./initial-meta-info-mapping";
import { isValidHTTPUrl, PORTAL_BASE_URL } from "../../../utils/url-utils";
import { GroupPermissionAttached } from "../group/grp_resource";
import { PermissionSet } from "../rbac/permission_set";
import { AllPermissions } from "../rbac/rbac";
import { validateCurrentCustomPathFormat } from "../../../components/organisms/smart-apply/procedure-detail/custom-path-setting/custom-path-validator";

export const ProcedureConsts = {
  MAX_SUMMARY_LENGTH: 200,
  MAX_NAME_LENGTH: 64,
  MAX_CUSTOM_PATH_LENGTH: 64,
  MAX_DETAIL_URL_LENGTH: 2000,
  MAX_DESCRIPTION_MARKDOWN_LENGTH: 500,
};

export type ProcedureType = core.ProcedureType;
export type BaseProcedureType = core.BaseProcedureType;

export type PaymentType = core.PaymentType;
export type BasicProcedureDetailProps = Omit<
  ProcedureDetailType,
  "template" | "applicantSchemaKeys"
>;

export type PublishStatus = core.PublishStatus;
export type PublishSetting = core.PublishSetting;
export type ProcedureDetailType = core.ProcedureDetailType;

export const customUrlAllowedCharRegex = /^[a-zA-Z0-9-]+$/;
export const procedureIdFormatRegex = /^[0-9]{19}$/;
export const procedureFormAccessRestrictionPasscodeRegex =
  /^[a-zA-Z0-9]{6,16}$/;

export class ProcedureEntity
  extends core.ProcedureEntity
  implements GroupPermissionAttached
{
  get outputBasePDFBinaryURL() {
    return `/api/v1/procedures/${this.id}/output-pdf-binary`;
  }

  isCopiable() {
    return !this.customTag;
  }

  isDraft() {
    return this.publishStatus === "DRAFT";
  }

  /**
   * 公開済みの場合は、公開しているdetail、下書きの状態ではdraftのdetailを取得する
   */
  getLiveDetail() {
    return this.isPublished() ? this.published! : this.draft;
  }

  getInitialDescriptionMarkdown() {
    if (!this.descriptionMarkdown && this.summary) {
      return this.detailURL
        ? `${this.summary}\n[制度詳細URLはこちら](${this.detailURL})`
        : this.summary;
    }
    return this.descriptionMarkdown;
  }

  isPublished(): boolean {
    return !!this.published;
  }

  unPublishableError(): string | undefined {
    const validator = new SmartApplyFormSchemaValidator(
      this.draft.template.formSchema,
      this.draft.applicantSchemaKeys,
    );
    // FIXME: 2020/11/17
    // サーバサイドとロジックが異なる。
    // 申請フォームのチェックのみ。決済設定と認証レベル設定のロジックが抜けている。
    const opt: ValidateOpt = {
      withJPKI: this.requireJPKISignature(),
      withMOJ: this.requireMOJSignature(),
    };
    if (validator.checkPublishable(opt)) return undefined;
    return validator.publishable(opt)[0] || "申請フォームを設定してください。";
  }

  isUnPublishable() {
    return !["DRAFT", "UNPUBLISHED"].includes(this.publishStatus);
  }

  hasSummaryWhenHavingDetailUrl() {
    return !this.detailURL || !!this.summary;
  }

  hasValidURL() {
    if (!this.detailURL) {
      return true;
    }
    if (!isValidHTTPUrl(this.detailURL)) {
      return false;
    }
    return this.detailURL.length <= ProcedureConsts.MAX_DETAIL_URL_LENGTH;
  }

  hasValidName() {
    return (
      this.name.length > 0 &&
      this.name.length <= ProcedureConsts.MAX_NAME_LENGTH
    );
  }

  hasValidSummary() {
    return (this.summary?.length || 0) <= ProcedureConsts.MAX_SUMMARY_LENGTH;
  }

  hasValidDescriptionMarkdown() {
    return (
      (this.descriptionMarkdown?.length || 0) <=
      ProcedureConsts.MAX_DESCRIPTION_MARKDOWN_LENGTH
    );
  }

  hasValidFormAccessRestriction(): boolean {
    if (!this.formAccessRestriction) {
      return true;
    }

    const { passcode } = this.formAccessRestriction;
    if (passcode === null || typeof passcode === "undefined") {
      return true;
    }

    return !!passcode.match(procedureFormAccessRestrictionPasscodeRegex);
  }

  isFormAccessRestricted(): boolean {
    // TODO: 制限方法が拡張された場合、追記する。
    return typeof this.formAccessRestriction?.passcode === "string";
  }

  requireSignature() {
    return this.requireJPKISignature() || this.requireMOJSignature();
  }

  requireJPKISignature() {
    const target = this.published ? this.published : this.draft;
    return target.requireAuthLevel.individual === "MY_NUMBER_CARD_SIGNATURE";
  }

  requireMOJSignature() {
    const target = this.published ? this.published : this.draft;
    return target.requireAuthLevel.corporation === "MOJ_SIGNATURE";
  }

  metaInfoSchema() {
    const schema: FormSchema =
      this.draft.template.pdfOutputSetting &&
      this.draft.template.pdfOutputSetting.metaInfoFormSchema
        ? FormSchema.fromJSON(
            this.draft.template.pdfOutputSetting.metaInfoFormSchema,
          )
        : FormSchema.fromJSON(InitialMetaInfoMapping);
    return schema.flatten().length === 0 ? InitialMetaInfoMapping : schema;
  }

  resolvePermission(groupId: string): PermissionSet {
    if (this.groupIds && this.groupIds.includes(groupId)) {
      // 現時点では、resource <> group間での権限の差をつける機能は提供していないため、
      // 全ての権限を付ける
      return new PermissionSet(AllPermissions);
    }
    return new PermissionSet([]);
  }

  get formulas() {
    const detail = this.getLiveDetail();
    const schema = CloudGovFormSchema.fromJSON(detail.template.formSchema);
    return schema.getFormulas();
  }

  allCSVMetaField(): { id: string; name: string; default: boolean }[] {
    let fields = [
      { id: "id", name: "申請ID", default: true },
      { id: "createdAt", name: "申請日時", default: true },
      { id: "modifiedAt", name: "最終訂正日時", default: false },
      { id: "status", name: "ステータス", default: true },
    ];
    if (this.requireJPKISignature()) {
      fields = [
        ...fields,
        { id: "signature_status", name: "電子署名確認結果", default: true },
      ];
    }

    return fields;
  }

  canDelete() {
    return this.isDraft();
  }

  canUpdateBasicInfo(
    existingCustomPath: string | undefined,
    newCustomPath: string | undefined,
  ): boolean {
    return (
      this.hasValidURL() &&
      this.hasSummaryWhenHavingDetailUrl() &&
      this.hasValidSummary() &&
      this.hasValidDescriptionMarkdown() &&
      this.hasValidName() &&
      this.hasValidFormAccessRestriction() &&
      !validateCurrentCustomPathFormat(newCustomPath, !!existingCustomPath)
    );
  }

  copy() {
    return new ProcedureEntity(this);
  }

  doorPageURL(orgSlug: string, currentCustomPath: string | undefined): string {
    return currentCustomPath
      ? `${PORTAL_BASE_URL}/${orgSlug}/smart-apply/apply-procedure-alias/${currentCustomPath}`
      : `${PORTAL_BASE_URL}/${orgSlug}/smart-apply/apply-procedure/${this.id}`;
  }

  previewDoorURL(host: string, orgSlug: string, previewToken: string): string {
    return `${host}/${orgSlug}/smart-apply/apply-procedure/${this.id}?applyPreviewToken=${previewToken}`;
  }

  canOutputPdf(): boolean {
    return (
      (!!this.published?.template?.pdfOutputSetting?.pdfOutputBaseFile ||
        !!this.published?.template?.pdfOutputSetting?.pdfOutputBaseFileURL) &&
      !!this.published?.template?.pdfOutputSetting?.metaInfoFormSchema
    );
  }

  hasPdfOutputSettingForDraft(): boolean {
    const setting = this.draft.template.pdfOutputSetting;
    if (!setting) {
      return false;
    }

    const { pdfOutputBaseFile, pdfOutputBaseFileURL, metaInfoFormSchema } =
      setting;
    return (
      (!!pdfOutputBaseFile || !!pdfOutputBaseFileURL) && !!metaInfoFormSchema
    );
  }

  hasCustomerDlPdfMapping() {
    return !!this.customerDlConfig?.pdfDownloadConfig;
  }

  customerDlPdfFileName() {
    const fileName = this.customerDlConfig?.pdfDownloadConfig?.fileName;
    if (typeof fileName === "string" && fileName.length > 0) {
      return fileName;
    }

    return this.published?.template.pdfOutputSetting?.pdfOutputBaseFileName;
  }

  isArchived() {
    return this.archivedAtUnixTime > 0 && this.archivedBy !== "";
  }

  static fromJSON(json: ProcedureType) {
    return new ProcedureEntity(json);
  }

  static fromJSONArray(json?: ProcedureType[]) {
    if (!json) {
      return [];
    }
    return json.map(ProcedureEntity.fromJSON);
  }
}

export class BaseProcedureEntity extends core.BaseProcedureEntity {
  static fromJSON(json: BaseProcedureType) {
    return new BaseProcedureEntity(json);
  }

  static fromJSONArray(json?: BaseProcedureType[]) {
    if (!json) {
      return [];
    }
    return json.map(BaseProcedureEntity.fromJSON);
  }
}
